/**
 * Map Filter Panel - Campaign and Flyer Type filters
 */
import { useTranslation } from "react-i18next";
import "./MapFilterPanel.css";

export interface Campaign {
    id: string;
    name: string;
}

export interface FlyerType {
    id: string;
    name: string;
}

export type MapMode = "flyer" | "poster";

export interface MapFilterPanelProps {
    campaigns?: Campaign[];
    flyerTypes?: FlyerType[];
    selectedCampaignId?: string;
    selectedFlyerTypeId?: string | null;
    onCampaignChange?: (campaignId: string) => void;
    onFlyerTypeChange?: (flyerTypeId: string | null) => void;
    isLoading?: boolean;
    mapMode?: MapMode;
    onMapModeChange?: (mode: MapMode) => void;
}

export const MapFilterPanel = ({
    campaigns = [],
    flyerTypes = [],
    selectedCampaignId,
    selectedFlyerTypeId,
    onCampaignChange,
    onFlyerTypeChange,
    isLoading = false,
    mapMode = "flyer",
    onMapModeChange
}: MapFilterPanelProps): JSX.Element => {
    const { t } = useTranslation();

    const handleCampaignChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const campaignId = e.target.value;
        if (onCampaignChange) {
            // Always call onCampaignChange, even if campaignId is empty (to clear selection)
            onCampaignChange(campaignId || "");
        }
    };

    const handleFlyerTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const flyerTypeId = e.target.value || null;
        if (onFlyerTypeChange) {
            onFlyerTypeChange(flyerTypeId);
        }
    };

    const handleClearFlyerFilter = () => {
        if (onFlyerTypeChange) {
            onFlyerTypeChange(null);
        }
    };

    return (
        <div className="map-filter-panel" role="region" aria-label={t("map.filter.title")}>
            <h3 className="map-filter-panel__title">{t("map.filter.title")}</h3>

            <div className="map-filter-panel__section">
                <label htmlFor="campaign-filter" className="map-filter-panel__label">
                    {t("map.filter.campaign")}
                </label>
                <select
                    id="campaign-filter"
                    className="map-filter-panel__select"
                    value={selectedCampaignId ?? ""}
                    onChange={handleCampaignChange}
                    disabled={isLoading || campaigns.length === 0}
                    aria-label={t("map.filter.campaign")}
                >
                    {campaigns.length === 0 ? (
                        <option value="">{t("map.filter.noCampaigns")}</option>
                    ) : (
                        <>
                            <option value="">{t("map.filter.selectCampaign")}</option>
                            {campaigns.map((campaign) => (
                                <option key={campaign.id} value={campaign.id}>
                                    {campaign.name}
                                </option>
                            ))}
                        </>
                    )}
                </select>
            </div>

            {selectedCampaignId && (
                <div className="map-filter-panel__section">
                    <div className="map-filter-panel__label-row">
                        <label htmlFor="flyer-filter" className="map-filter-panel__label">
                            {t("map.filter.flyerType")}
                        </label>
                        {selectedFlyerTypeId && (
                            <button
                                type="button"
                                className="map-filter-panel__clear"
                                onClick={handleClearFlyerFilter}
                                aria-label={t("map.filter.clearFlyer")}
                            >
                                {t("map.filter.clear")}
                            </button>
                        )}
                    </div>
                    <select
                        id="flyer-filter"
                        className="map-filter-panel__select"
                        value={selectedFlyerTypeId ?? ""}
                        onChange={handleFlyerTypeChange}
                        disabled={isLoading || flyerTypes.length === 0}
                        aria-label={t("map.filter.flyerType")}
                    >
                        {flyerTypes.length === 0 ? (
                            <option value="">{t("map.filter.noFlyerTypes")}</option>
                        ) : (
                            <>
                                <option value="">{t("map.filter.allFlyers")}</option>
                                {flyerTypes
                                    .filter((flyer): flyer is FlyerType => flyer !== null && flyer !== undefined)
                                    .map((flyer) => (
                                        <option key={flyer.id} value={flyer.id}>
                                            {flyer.name}
                                        </option>
                                    ))}
                            </>
                        )}
                    </select>
                </div>
            )}

            {onMapModeChange && (
                <div className="map-filter-panel__section map-filter-panel__section--mode">
                    <button
                        type="button"
                        className={`map-filter-panel__mode-btn${mapMode === "poster" ? " map-filter-panel__mode-btn--active" : ""}`}
                        onClick={() => onMapModeChange(mapMode === "poster" ? "flyer" : "poster")}
                        aria-pressed={mapMode === "poster"}
                    >
                        {mapMode === "poster" ? "🪧 Affischläge" : "📄 Flygbladsläge"}
                    </button>
                </div>
            )}

            {isLoading && (
                <div className="map-filter-panel__loading" aria-live="polite">
                    {t("map.filter.loading")}
                </div>
            )}
        </div>
    );
};